import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormControl,
  Grid,
  Input,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useState } from "react";

import Alert from "components/Alert";
import { fetcherAuth } from "utils/api";
import { normalizePhone } from "utils/form";
import { useAlert } from "utils/useAlert";

import styles from "../styles";

const TextCampaign = () => {
  const [message, severity, setAlert, closeAlert] = useAlert();
  const { getToken } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    setFile(selectedFile || null);
  };
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [failedSends, setFailedSends] = useState<string[]>([]);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (!file) {
        throw new Error("You must select a CSV file to upload");
      }
      const data = _generateFormData();
      const res = await fetcherAuth(
        getToken,
        `/api/admin/text-campaigns/send`,
        "POST",
        {},
        data,
        false,
        true,
      );
      if (res.failed && res.failed?.length > 0) {
        setFailedSends(res.failed);
      } else {
        setFailedSends([]);
      }
      if (res.successCount)
        setAlert(
          `Successfully sent to ${res.successCount} recipients`,
          "success",
        );
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    setLoading(true);
    try {
      if (!file) {
        throw new Error("You must select a CSV file to upload");
      }
      const data = _generateFormData();
      const res = await fetcherAuth(
        getToken,
        `/api/admin/text-campaigns/send-test`,
        "POST",
        {},
        data,
        false,
        true,
      );
      if (res.error) {
        setFailedSends([res.error]);
      } else {
        setFailedSends([]);
        setAlert(
          `Successfully sent test message to ${testPhoneNumber}`,
          "success",
        );
      }
    } catch (error) {
      setAlert(error?.message, "warning");
    } finally {
      setLoading(false);
    }
  };

  const _generateFormData = () => {
    if (!file) {
      throw new Error("You must select a CSV file to upload");
    }
    const data = new FormData();
    const fileExt = file.name.split(".").pop()?.toLowerCase();
    if (fileExt !== "csv") {
      throw new Error("You must select a CSV file");
    }
    data.append("file", file);
    data.append("body", body);
    if (testPhoneNumber) {
      data.append("toNumber", testPhoneNumber);
    }
    return data;
  };

  return (
    <Box>
      <Typography variant="h4">Text Campaign </Typography>

      <Grid container gap={1}>
        <LoadingButton
          variant="contained"
          disableElevation
          sx={{ mt: 2 }}
          onClick={handleSubmit}
          loading={loading}
          disabled={!file || body.trim() === ""}
        >
          Send Text Message{" "}
          <Box
            component="i"
            className="fa-regular fa-paper-plane"
            sx={{ ml: 1 }}
          />
        </LoadingButton>
      </Grid>

      <Grid container gap={3}>
        <Grid item xs={6}>
          <Typography sx={{ mt: 3, mb: 1 }}>Text Message Body</Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            placeholder="Hi {name}, Bento just launched something for you..."
            onChange={(e) => setBody(e.target.value)}
          />

          <Typography sx={{ mt: 2, mb: 1 }}>
            Test the message before sending to all users in CSV
          </Typography>

          <Grid container alignItems="center">
            <TextField
              placeholder="Test Phone Number"
              onChange={(e) => setTestPhoneNumber(e.target.value)}
              sx={{ width: 300 }}
              value={normalizePhone(testPhoneNumber || "")}
              size="small"
            />
            <LoadingButton
              variant="outlined"
              disableElevation
              sx={{ ml: 1 }}
              loading={loading}
              disabled={!file || body.trim() === "" || !testPhoneNumber}
              onClick={handleTest}
            >
              Send Test Sample{" "}
              <Box component="i" className="fa-regular fa-eye" sx={{ ml: 1 }} />
            </LoadingButton>
          </Grid>
        </Grid>
        <Grid item xs>
          <Typography variant="h6">CSV of Recipients</Typography>

          <Typography sx={{ maxWidth: 600 }}>
            <strong>Important rules to note when uploading:</strong>

            <li>
              You must have either a column named{" "}
              <strong>organization_id</strong> or a column name{" "}
              <strong>phone_number</strong>. If the{" "}
              <strong>phone_number</strong> column is provided, it will be used.
              Else, we will fetch their OrganizationProfile to get their phone
              number.
            </li>
            <li>
              If you use a variable in the email, make sure the variable match
              exactly the column's name. (e.g{" "}
              <em>
                <strong>
                  There are &#123;X&#125; contacts to reach out...
                </strong>
              </em>{" "}
              then the CSV must have a column named{" "}
              <em>
                <strong>X</strong>
              </em>
              )
            </li>
            <li>
              At the moment, only phone numbers in{" "}
              <strong>U.S or Canada</strong> can receive texts.
            </li>
            <FormControl sx={styles.file}>
              <Input type="file" onChange={handleFileChange} />
            </FormControl>
          </Typography>

          {failedSends && failedSends.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" color="error">
                These messages failed to send:
              </Typography>
              {failedSends.map((fail, index) => (
                <Typography
                  key={`fail-${index}`}
                  sx={{ color: "error.main" }}
                  gutterBottom
                >
                  {fail}
                </Typography>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
      <Alert message={message} severity={severity} closeAlert={closeAlert} />
    </Box>
  );
};

export default TextCampaign;
